import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPinned, Loader2, FileDown } from "lucide-react";
import ValutazioneImmobilart from "@/components/ValutazioneImmobilart";

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 }));
const TIP_MAP = [[/villa|villino/i, "Ville e Villini"], [/box|garage|posto auto/i, "Box"], [/negozi/i, "Negozi"], [/uffic/i, "Uffici"], [/econom/i, "Abitazioni di tipo economico"]];
const tipDefault = (t) => (TIP_MAP.find(([re]) => re.test(t || "")) || [null, "Abitazioni civili"])[1];
const MOTIVI = { indirizzo_non_trovato: "Indirizzo non trovato sulla mappa.", fuori_zone: "L'indirizzo è fuori dalle zone OMI caricate.", indirizzo_mancante: "Manca l'indirizzo.", dati_mancanti: "Dati OMI non ancora caricati (Impostazioni → Dati OMI)." };

function Forbice({ label, min, max, mq, unit, testid }) {
  return (
    <div className="p-3 rounded-xl bg-[#F7F7F8] border border-[var(--border)]" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">{label}</div>
      <div className="font-display font-bold text-lg mono">{eur(min)} – {eur(max)} <span className="text-xs font-medium text-[var(--muted)]">{unit}</span></div>
      {mq > 0 && min != null && <div className="text-sm text-slate-700 mt-0.5">× {mq} mq = <b className="mono">€ {eur(Math.round(min * mq))} – {eur(Math.round(max * mq))}</b>{unit.includes("mese") ? " al mese" : ""}</div>}
    </div>
  );
}

export default function OmiCard({ tipo, id, indirizzo, comune, mq, tipologia, prezzo, onChange, draft, onDraft }) {
  const [d, setD] = useState(null);
  const [zone, setZone] = useState([]);
  const [sel, setSel] = useState({ tipologia: tipDefault(tipologia), stato: "NORMALE", mq: mq || "" });
  const [busy, setBusy] = useState(false);
  const [schede, setSchede] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const loadSchede = () => api.get(`/omi/schede/${tipo}/${id}`).then((r) => setSchede(r.data)).catch(() => {});
  const scarica = async (fid, nome) => {
    const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = nome; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  // Con onDraft (scheda Immobile) le scelte restano in bozza fino a "Salva"; altrimenti salvataggio diretto.
  const salva = (patch) => (onDraft ? onDraft({ ...(draft || {}), ...patch }) : api.put(`/omi/scelta/${tipo}/${id}`, patch).catch(() => {}));
  const cerca = async (link_zona) => {
    setBusy(true);
    try {
      const r = await api.get("/omi/valuta", { params: link_zona ? { link_zona } : { indirizzo, comune } });
      setD(r.data);
      if (!r.data.trovata && !zone.length) api.get("/omi/zone", { params: { comune } }).then((z) => setZone(z.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => {
    api.get(`/omi/scelta/${tipo}/${id}`).then((r) => {
      const s = r.data || {};
      setSel((p) => ({ tipologia: s.tipologia || p.tipologia, stato: s.stato || p.stato, mq: s.mq || p.mq }));
      cerca(s.link_zona);
    }).catch(() => cerca());
    loadSchede();
  }, [tipo, id, indirizzo, comune]);
  const scegliZona = (lz) => { if (!lz) return; salva({ link_zona: lz }); cerca(lz); };
  const setS = (k, v) => { if (!onDraft) setSel((p) => ({ ...p, [k]: v })); salva({ [k]: v }); };
  const selV = onDraft ? { ...sel, ...(draft || {}) } : sel;
  const tipologie = d?.valori ? [...new Set(d.valori.map((v) => v.tipologia))] : [];
  const stati = d?.valori ? [...new Set(d.valori.filter((v) => v.tipologia === selV.tipologia).map((v) => v.stato))] : [];
  const v = d?.valori?.find((x) => x.tipologia === selV.tipologia && x.stato === selV.stato) || d?.valori?.find((x) => x.tipologia === selV.tipologia);
  const m = Number(selV.mq) || 0;
  const generaPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.post(`/omi/scheda-pdf/${tipo}/${id}`, { link_zona: d.link_zona, tipologia: v.tipologia, stato: v.stato, mq: selV.mq || mq, indirizzo }, { timeout: 60000 });
      const bin = Uint8Array.from(atob(r.data.pdf_base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bin], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = r.data.nome; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      loadSchede();
      if (r.data.salvato) toast.success("Scheda PDF salvata nello storico");
      else toast.warning("PDF scaricato, ma non salvato nello storico: riprova più tardi per archiviarlo.");
    } catch (e) {
      const st = e.response?.status;
      const det = typeof e.response?.data?.detail === "string" ? e.response.data.detail : null;
      toast.error(det || (st >= 500 || !st || e.code === "ECONNABORTED" ? "Il server non ha risposto in tempo durante la creazione del PDF. Riprova tra qualche istante; se il problema continua avvisa l'assistenza." : apiError(e)));
    } finally { setPdfBusy(false); }
  };
  return (
    <div className="card-immo p-5 mb-6" data-testid="omi-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-xl bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center"><MapPinned size={16} /></span>
        <h2 className="font-display font-bold text-base flex-1">Valore OMI</h2>
        {busy && <Loader2 size={16} className="animate-spin text-[var(--muted)]" />}
      </div>
      {d && !d.trovata && (
        <div className="mb-3" data-testid="omi-non-trovata">
          <p className="text-sm text-amber-700 mb-2">{MOTIVI[d.motivo] || "Zona non trovata."} {d.motivo !== "dati_mancanti" && "Scegli la zona dall'elenco:"}</p>
          {d.motivo !== "dati_mancanti" && (
            <select data-testid="omi-zona-select" defaultValue="" onChange={(e) => scegliZona(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white">
              <option value="">— Seleziona zona OMI —</option>
              {zone.map((z) => <option key={z.link_zona} value={z.link_zona}>{z.comune} · {z.zona} {z.zona_descr}</option>)}
            </select>
          )}
        </div>
      )}
      {d?.trovata && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3 text-sm" data-testid="omi-zona">
            <span className="px-2.5 py-1 rounded-full bg-[#111111] text-white font-bold text-xs">{d.zona}</span>
            <span className="font-semibold">{d.zona_descr}</span>
            <span className="text-[var(--muted)]">· {d.comune} · fascia {d.fascia} · {d.metodo === "manuale" ? "zona scelta a mano" : "da indirizzo"}</span>
            <button data-testid="omi-cambia-zona" onClick={() => { setD({ trovata: false, motivo: "manuale" }); api.get("/omi/zone", { params: { comune } }).then((z) => setZone(z.data)); }} className="text-xs font-semibold text-[#FC82ED] underline">cambia</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <select data-testid="omi-tipologia" value={selV.tipologia} onChange={(e) => setS("tipologia", e.target.value)} className="px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white">
              {tipologie.map((t) => <option key={t}>{t}</option>)}
            </select>
            <select data-testid="omi-stato" value={selV.stato} onChange={(e) => setS("stato", e.target.value)} className="px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white">
              {(stati.length ? stati : ["NORMALE"]).map((s) => <option key={s}>{s}</option>)}
            </select>
            <div className="flex items-center gap-1.5">
              <input data-testid="omi-mq" type="number" min="0" value={selV.mq} onChange={(e) => (onDraft ? salva({ mq: e.target.value }) : setSel((p) => ({ ...p, mq: e.target.value })))} onBlur={(e) => { if (!onDraft) salva({ mq: e.target.value }); }}
                placeholder="mq" className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm" />
              <span className="text-xs text-[var(--muted)]">mq</span>
            </div>
          </div>
          {v ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Forbice testid="omi-compravendita" label={`Compravendita (sup. ${v.sup_compr === "N" ? "netta" : "lorda"})`} min={v.compr_min} max={v.compr_max} mq={m} unit="€/mq" />
              <Forbice testid="omi-affitto" label={`Affitto (sup. ${v.sup_loc === "N" ? "netta" : "lorda"})`} min={v.loc_min} max={v.loc_max} mq={m} unit="€/mq al mese" />
            </div>
          ) : <p className="text-sm text-[var(--muted)]">Nessun valore OMI per questa tipologia in questa zona.</p>}
          {v && v.stato !== selV.stato && <p className="text-[11px] text-amber-700 mt-1">Stato {selV.stato} non disponibile: mostro {v.stato}.</p>}
          {v && (
            <button data-testid="omi-scarica-pdf" onClick={generaPdf} disabled={pdfBusy}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold disabled:opacity-50">
              {pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Scarica scheda PDF
            </button>
          )}
        </>
      )}
      <ValutazioneImmobilart tipo={tipo} id={id} mq={m || Number(mq) || 0} prezzo={prezzo} onChange={onChange}
        omi={v ? { zona: d.zona, tipologia: v.tipologia, stato: v.stato, compr_min: v.compr_min, compr_max: v.compr_max, semestre: d.semestre } : null} />
      {schede.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid="omi-schede">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1">Schede generate</div>
          {schede.map((s) => (
            <button key={s.id} data-testid={`omi-scheda-${s.id}`} onClick={() => scarica(s.id, s.nome).catch((e) => toast.error(apiError(e)))}
              className="w-full flex items-center justify-between gap-2 py-1.5 text-left text-sm hover:text-[#FC82ED]">
              <span className="truncate">{s.nome}</span><span className="text-[11px] text-[var(--muted)] shrink-0">{s.by}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-[var(--muted)] mt-3" data-testid="omi-fonte">Fonte: Agenzia Entrate – OMI, semestre {d?.semestre || "—"}</p>
    </div>
  );
}

export function OmiImportCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/omi/stato").then((r) => setS(r.data)).catch(() => setS({}));
  useEffect(() => { load(); }, []);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/omi/import", fd);
      toast.success(`Dati OMI ${r.data.semestre || ""} aggiunti: ${r.data.zone} zone, ${r.data.valori} valori, ${r.data.poligoni} perimetri`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const elimina = async (p) => {
    if (!window.confirm(`Eliminare i dati OMI della provincia ${p.prov} (semestre ${p.semestre})?`)) return;
    try { await api.delete(`/omi/provincia/${p.prov}`, { params: { semestre: p.solo_perimetri ? "" : p.semestre } }); toast.success("Provincia eliminata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const prov = s?.province || [];
  return (
    <div className="card-immo p-5" data-testid="omi-import-card">
      <div className="flex items-center gap-2 mb-1"><span className="w-8 h-8 rounded-xl bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center"><MapPinned size={16} /></span><h2 className="font-display font-bold text-base">Dati OMI</h2></div>
      <p className="text-xs text-[var(--muted)] mb-3">Ogni ZIP caricato si AGGIUNGE: sostituisce solo i comuni presenti nel nuovo file per lo stesso semestre. Accetta ZIP completi, solo CSV (anche di una regione) o solo perimetri KML.</p>
      {prov.length === 0 ? <div className="text-sm text-amber-700 mb-3">Nessun dato OMI caricato.</div> : (
        <div className="space-y-2 mb-3" data-testid="omi-stato">
          {prov.map((p) => (
            <div key={`${p.prov}-${p.semestre}`} data-testid={`omi-prov-${p.prov}-${p.semestre.replace("/", "-")}`} className="p-3 rounded-xl border border-[var(--border)] bg-[#F7F7F8]">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold">{p.prov} · semestre {p.semestre}</div>
                  <div className="text-xs text-slate-600">{p.comuni} comuni · {p.zone} zone · {p.valori} valori · {p.poligoni} perimetri</div>
                  <div className="text-[11px] text-[var(--muted)]">{p.caricato_at ? new Date(p.caricato_at).toLocaleString("it-IT") : ""}{p.caricato_da ? ` · ${p.caricato_da}` : ""}</div>
                </div>
                <button data-testid={`omi-elimina-${p.prov}`} onClick={() => elimina(p)} className="shrink-0 text-xs font-semibold text-red-500 px-2 py-1 rounded-lg hover:bg-red-50">Elimina</button>
              </div>
              {p.solo_perimetri && <div className="mt-1.5 text-[11px] text-amber-700">Solo perimetri: mancano i valori (carica i CSV).</div>}
              {p.comuni_senza_perimetri.length > 0 && (
                <div data-testid={`omi-senza-perimetri-${p.prov}`} className="mt-1.5 text-[11px] text-amber-700">
                  {p.comuni_senza_perimetri.length} comuni con valori ma senza perimetri (la zona andrà scelta a mano): {p.comuni_senza_perimetri.slice(0, 8).join(", ")}{p.comuni_senza_perimetri.length > 8 ? "…" : ""}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <label className={`block text-center py-2.5 rounded-xl text-sm font-semibold cursor-pointer ${busy ? "bg-slate-200 text-slate-500" : "bg-[#111111] text-white"}`}>
        {busy ? "Caricamento…" : "Carica ZIP OMI"}
        <input data-testid="omi-import-input" type="file" accept=".zip" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
}
